/**
 * Cart → fordon/kapacitet för kundtransportpris.
 * Prioritet: produktfält (DB) → slug-map → namn/kategori-heuristik.
 * Studentflak m.m. med transport ingår: kind = excluded (räknas som 0).
 */

export type TransportUnitKind =
  | 'tops'
  | 'sub'
  | 'light_package'
  | 'flightcase_unit'
  | 'stage'
  | 'package'
  | 'other'
  | 'excluded';

export type CartTransportLine = {
  slug?: string | null;
  name?: string | null;
  category?: string | null;
  quantity: number;
  /** Från products.transport_kind — null/undefined = fallback. */
  transportKind?: TransportUnitKind | null;
  /** Från products.transport_units. */
  unitsPerItem?: number | null;
  transportTops?: number | null;
  transportSubs?: number | null;
  transportFlightcases?: number | null;
};

export type TransportCapacityCounts = {
  tops: number;
  sub: number;
  lightPackage: number;
  flightcaseUnit: number;
  /** Antal moving heads (DMX) — separat från övriga flightcases. */
  movingHead: number;
  stage: number;
  other: number;
};

export type TransportCapacityResult = {
  counts: TransportCapacityCounts;
  rentalVehicleRequired: boolean;
  reason:
    | 'none'
    | 'two_or_more_subs'
    | 'tops_sub_and_flightcase'
    | 'flightcase_over_four'
    | 'stage_equipment';
  customerHint: string | null;
};

export const TRANSPORT_KIND_OPTIONS: ReadonlyArray<{
  value: TransportUnitKind | '';
  label: string;
}> = [
  { value: '', label: 'Automatisk (slug/heuristik)' },
  { value: 'tops', label: 'Topphögtalare' },
  { value: 'sub', label: 'Subwoofer' },
  { value: 'light_package', label: 'Ljuspaket (personbil)' },
  { value: 'flightcase_unit', label: 'Flightcase-enhet' },
  { value: 'stage', label: 'Scen / tross' },
  { value: 'package', label: 'Paket (ange innehåll)' },
  { value: 'other', label: 'Övrigt' },
  { value: 'excluded', label: 'Räkna inte / transport ingår' },
];

/** Explicit slug → kind (fallback när DB-fält saknas). */
export const TRANSPORT_KIND_BY_SLUG: Readonly<Record<string, TransportUnitKind>> = {
  // Tops
  '2-x-alto-ts415-5000w': 'tops',
  '2-x-premiumhogtalare-5000w': 'tops',
  'soundboks-gen-3-portabel-festhogtalare-med-extremt-tryck': 'tops',
  'mackie-thump-go-batteridrivet-pa-system-2-st': 'tops',
  'premium-rcf-art-912-a-dubbel-inkl-stativ': 'tops',
  'premium-rcf-art-415-a-dubbel-inkl-stativ': 'tops',
  'premium-rcf-nxl-24-a-mk2-2-st': 'tops',
  'd-b-xsl8': 'tops',
  'd-b-xsl12': 'tops',

  // Subs
  'subwoofer-kraftfull-och-djup-bas': 'sub',
  'premium-rcf-sub-18-ax': 'sub',
  'rcf-sub-9029-as-professionell-dubbel-19-subwoofer': 'sub',
  'd-b-sl-gsub': 'sub',

  // Paket (expansion via PACKAGE_EXPANSION)
  'lilla-ljudpaketet': 'package',
  'mellan-ljudpaketet': 'package',
  'mellan-ljudpaketet-5300w': 'package',
  'stora-ljudpaketet-7800w': 'package',
  'hogtalarpaket-10-000w': 'package',
  'premium-rcf-nxl-24-a-mk2-sub-18-ax-komplett-ljudsystem': 'package',
  'lilla-festpaketet': 'package',
  'standard-festpaket': 'package',
  'stora-festpaketet-10-000-w-ljud-maxad-ljusshow-total-partykansla': 'package',

  // Studentflak — transport ingår, räkna inte
  'stora-studentflaket': 'excluded',
  'lilla-studentflaket': 'excluded',
  'studentflak-xxl': 'excluded',
  'studentflak-xxl-4-topp-4-subbar': 'excluded',

  'rcf-hdl-6-a-kompakt-line-array': 'stage',

  // Ljuspaket
  'eventpaket-pro-ljus-allt-du-behover-for-riktig-showkansla': 'light_package',
  'gigbar-move-ils-allt-i-ett-ljuseffekt-for-fest-scen-brollop': 'light_package',
  'mellan-ljuspaketet-effekt-fargsattning': 'light_package',
  'stora-ljuspaketet-effektljus-rokmaskin': 'light_package',
  'lilla-ljuspaketet-smidig-effektfull-festbelysning': 'light_package',

  // Karaoke / AV (inte PA)
  'karaoke-quizz': 'other',

  // Effekter
  'vertical-fogger': 'flightcase_unit',

  // DJ
  'pioneer-xdj-rx3-all-in-one-dj-system-i-absolut-toppklass': 'other',

  // Flightcase / snabba effekter
  'konfettimaskin-kraftfull-1500w-med-rgb-ljus-justerbar-kastvinkel': 'flightcase_unit',
  'premium-konfettikanon': 'flightcase_unit',
  'konfettikanon-skjuter-konfettiror': 'flightcase_unit',
  'omgivningsbelysning-10-pack-uplights': 'flightcase_unit',
  lagrokmaskin: 'flightcase_unit',
  'moving-head-spot': 'flightcase_unit',
  'moving-head-wash': 'flightcase_unit',
  'moving-head-beam': 'flightcase_unit',
  'hybrid-moving-head-beam-spot-wash': 'flightcase_unit',
  'kraftfulla-moving-head-wash-lampor-2-pack': 'flightcase_unit',
  '4-x-moving-head-wash-for-fest-scen-event': 'flightcase_unit',
  'co2-jet-kraftfull-scen-effekt-for-event-klubb-brollop': 'other',

  'scener-scenpodier-tross-kravallstaket-offertbaserade-losningar': 'stage',
};

export const TRANSPORT_UNITS_PER_ITEM_BY_SLUG: Readonly<Record<string, number>> = {
  'kraftfulla-moving-head-wash-lampor-2-pack': 2,
  '4-x-moving-head-wash-for-fest-scen-event': 4,
  '2-x-alto-ts415-5000w': 2,
  '2-x-premiumhogtalare-5000w': 2,
  'mackie-thump-go-batteridrivet-pa-system-2-st': 2,
  'premium-rcf-art-912-a-dubbel-inkl-stativ': 2,
  'premium-rcf-art-415-a-dubbel-inkl-stativ': 2,
  'premium-rcf-nxl-24-a-mk2-2-st': 2,
};

/** Paketexpansion (fallback när DB-fält saknas). Studentflak ingår inte. */
export const PACKAGE_EXPANSION: Readonly<
  Record<
    string,
    { tops?: number; sub?: number; flightcaseUnit?: number; stage?: number; lightPackage?: number }
  >
> = {
  'lilla-ljudpaketet': { tops: 2, sub: 0 },
  'mellan-ljudpaketet': { tops: 2, sub: 1 },
  'mellan-ljudpaketet-5300w': { tops: 2, sub: 1 },
  'stora-ljudpaketet-7800w': { tops: 2, sub: 2 },
  'hogtalarpaket-10-000w': { tops: 2, sub: 2 },
  'premium-rcf-nxl-24-a-mk2-sub-18-ax-komplett-ljudsystem': { tops: 2, sub: 1 },
  // Festpaket: lightPackage så montage blir medium (inte bara flightcase-tröskel)
  'lilla-festpaketet': { tops: 2, sub: 0, lightPackage: 1, flightcaseUnit: 1 },
  'standard-festpaket': { tops: 2, sub: 0, lightPackage: 1, flightcaseUnit: 2 },
  'stora-festpaketet-10-000-w-ljud-maxad-ljusshow-total-partykansla': {
    tops: 2,
    sub: 2,
    lightPackage: 1,
    flightcaseUnit: 3,
  },
};

function normalizeSlug(slug?: string | null): string {
  return String(slug ?? '')
    .trim()
    .toLowerCase();
}

function finiteNonNegInt(n: unknown, fallback = 0): number {
  const v = typeof n === 'number' ? n : Number(n);
  if (!Number.isFinite(v) || v < 0) return fallback;
  return Math.floor(v);
}

export function isMovingHeadLine(line: CartTransportLine): boolean {
  const slug = normalizeSlug(line.slug);
  const name = String(line.name ?? '').toLowerCase();
  return /moving.?head|moving-head/.test(`${name} ${slug}`);
}

export function isTransportKind(value: unknown): value is TransportUnitKind {
  return (
    value === 'tops' ||
    value === 'sub' ||
    value === 'light_package' ||
    value === 'flightcase_unit' ||
    value === 'stage' ||
    value === 'package' ||
    value === 'other' ||
    value === 'excluded'
  );
}

/** true om raden ska ignoreras i kapacitetsräkning. */
export function isExcludedTransportLine(line: CartTransportLine): boolean {
  if (line.transportKind === 'excluded') return true;
  const slug = normalizeSlug(line.slug);
  if (slug && TRANSPORT_KIND_BY_SLUG[slug] === 'excluded') return true;
  const category = String(line.category ?? '').toLowerCase();
  if (category.includes('studentflak') || slug.includes('studentflak')) return true;
  return false;
}

export function resolveTransportKind(line: CartTransportLine): TransportUnitKind {
  if (isTransportKind(line.transportKind)) return line.transportKind;

  const slug = normalizeSlug(line.slug);
  if (slug && TRANSPORT_KIND_BY_SLUG[slug]) return TRANSPORT_KIND_BY_SLUG[slug];

  if (isExcludedTransportLine(line)) return 'excluded';

  const name = String(line.name ?? '').toLowerCase();
  const category = String(line.category ?? '').toLowerCase();

  // CO₂ kan höja montagenivå (other). Övriga snabba effekter = flightcase utan montagebump.
  if (/co2/.test(name + slug) && !/tross|podium|kravall/.test(name)) {
    return 'other';
  }
  if (/konfetti|powershot|magic.?fx|rök|rok|haze|snö|sno|fogger/.test(name + slug) && !/tross|podium|kravall/.test(name)) {
    return 'flightcase_unit';
  }

  if (/tross|podium|kravall/.test(name) || category === 'scen') return 'stage';
  if (/\bscen\b/.test(name) && /scenpod|scener/.test(name + slug)) return 'stage';
  if (/subwoofer|\bsub\b|gsub/.test(name) || /subwoofer/.test(slug)) return 'sub';
  if (/moving.?head/.test(name) || /moving-head/.test(slug)) return 'flightcase_unit';
  if (/konfetti.*1500|lågrök|lagrok|omgivningsbelysning-10|10.?pack.?uplight/.test(name + slug)) {
    return 'flightcase_unit';
  }
  if (/ljuspaket|eventpaket.*ljus|gigbar/.test(name + slug) || (category === 'paket' && /ljus/.test(name))) {
    return 'light_package';
  }
  if (/högtalare|hogtalare|topp|xsl|art.?91|art.?41|nxl|soundboks|thump/.test(name + slug)) {
    return 'tops';
  }
  return 'other';
}

export function unitsForLine(line: CartTransportLine): number {
  const slug = normalizeSlug(line.slug);
  const fromProduct =
    line.unitsPerItem != null && Number.isFinite(line.unitsPerItem) && line.unitsPerItem >= 1
      ? Math.floor(line.unitsPerItem)
      : null;
  const perItem =
    fromProduct ??
    (slug && TRANSPORT_UNITS_PER_ITEM_BY_SLUG[slug] != null
      ? TRANSPORT_UNITS_PER_ITEM_BY_SLUG[slug]
      : 1);
  const qty = Number.isFinite(line.quantity) && line.quantity > 0 ? Math.floor(line.quantity) : 0;
  return qty * perItem;
}

function packageExpansionForLine(line: CartTransportLine): {
  tops: number;
  sub: number;
  flightcaseUnit: number;
  stage: number;
  lightPackage: number;
} | null {
  const kind = resolveTransportKind(line);
  if (kind === 'excluded') return null;

  const slug = normalizeSlug(line.slug);
  const exp = slug ? PACKAGE_EXPANSION[slug] : undefined;

  if (line.transportKind === 'package') {
    const dbFlight = line.transportFlightcases;
    const flight =
      dbFlight != null && Number(dbFlight) > 0
        ? finiteNonNegInt(dbFlight)
        : (exp?.flightcaseUnit ?? finiteNonNegInt(dbFlight));
    return {
      tops: finiteNonNegInt(line.transportTops, exp?.tops ?? 0),
      sub: finiteNonNegInt(line.transportSubs, exp?.sub ?? 0),
      flightcaseUnit: flight,
      stage: exp?.stage ?? 0,
      lightPackage: exp?.lightPackage ?? 0,
    };
  }

  if (kind === 'package' || (slug && PACKAGE_EXPANSION[slug])) {
    if (
      line.transportTops != null ||
      line.transportSubs != null ||
      line.transportFlightcases != null
    ) {
      const dbFlight = line.transportFlightcases;
      const flight =
        dbFlight != null && Number(dbFlight) > 0
          ? finiteNonNegInt(dbFlight)
          : (exp?.flightcaseUnit ?? finiteNonNegInt(dbFlight));
      return {
        tops: finiteNonNegInt(line.transportTops, exp?.tops ?? 0),
        sub: finiteNonNegInt(line.transportSubs, exp?.sub ?? 0),
        flightcaseUnit: flight,
        stage: exp?.stage ?? 0,
        lightPackage: exp?.lightPackage ?? 0,
      };
    }
    if (exp) {
      return {
        tops: exp.tops ?? 0,
        sub: exp.sub ?? 0,
        flightcaseUnit: exp.flightcaseUnit ?? 0,
        stage: exp.stage ?? 0,
        lightPackage: exp.lightPackage ?? 0,
      };
    }
  }

  return null;
}

export function countCartTransportUnits(lines: CartTransportLine[]): TransportCapacityCounts {
  const counts: TransportCapacityCounts = {
    tops: 0,
    sub: 0,
    lightPackage: 0,
    flightcaseUnit: 0,
    movingHead: 0,
    stage: 0,
    other: 0,
  };

  for (const line of lines) {
    const qty = Number.isFinite(line.quantity) && line.quantity > 0 ? Math.floor(line.quantity) : 0;
    if (qty <= 0) continue;
    if (isExcludedTransportLine(line)) continue;

    const kind = resolveTransportKind(line);
    if (kind === 'excluded') continue;

    // Explicit package from DB (komplettera flightcases från slug-expansion om DB saknar)
    if (line.transportKind === 'package') {
      const slug = normalizeSlug(line.slug);
      const exp = slug ? PACKAGE_EXPANSION[slug] : undefined;
      counts.tops += finiteNonNegInt(line.transportTops, exp?.tops ?? 0) * qty;
      counts.sub += finiteNonNegInt(line.transportSubs, exp?.sub ?? 0) * qty;
      const dbFlight = line.transportFlightcases;
      const flight =
        dbFlight != null && Number(dbFlight) > 0
          ? finiteNonNegInt(dbFlight)
          : (exp?.flightcaseUnit ?? finiteNonNegInt(dbFlight));
      counts.flightcaseUnit += flight * qty;
      counts.stage += (exp?.stage ?? 0) * qty;
      counts.lightPackage += (exp?.lightPackage ?? 0) * qty;
      continue;
    }

    const expansion = packageExpansionForLine(line);
    if (expansion && (kind === 'package' || normalizeSlug(line.slug) in PACKAGE_EXPANSION)) {
      counts.tops += expansion.tops * qty;
      counts.sub += expansion.sub * qty;
      counts.flightcaseUnit += expansion.flightcaseUnit * qty;
      counts.stage += expansion.stage * qty;
      counts.lightPackage += expansion.lightPackage * qty;
      continue;
    }

    const units = unitsForLine(line);
    switch (kind) {
      case 'tops':
        counts.tops += units;
        break;
      case 'sub':
        counts.sub += units;
        break;
      case 'light_package':
        counts.lightPackage += units;
        break;
      case 'flightcase_unit':
        counts.flightcaseUnit += units;
        if (isMovingHeadLine(line)) counts.movingHead += units;
        break;
      case 'stage':
        counts.stage += units;
        break;
      case 'package': {
        // package without expansion data — treat as other
        counts.other += units;
        break;
      }
      default:
        counts.other += units;
        break;
    }
  }

  return counts;
}

/**
 * Regler (OR):
 * 1. sub ≥ 2 → hyrbil
 * 2. tops ≥ 2 och sub ≥ 1 och minst 1 flightcase → hyrbil
 * 3. Enbart flightcase (utan skrymmande tops/sub/scen): > 4 → hyrbil
 * 4. Scen/tross → hyrbil
 */
export function evaluateCartTransportCapacity(lines: CartTransportLine[]): TransportCapacityResult {
  const counts = countCartTransportUnits(lines);
  const bulkyAudioOrStage = counts.tops > 0 || counts.sub > 0 || counts.stage > 0;

  if (counts.stage > 0) {
    return {
      counts,
      rentalVehicleRequired: true,
      reason: 'stage_equipment',
      customerHint: 'Utrustningen kräver större transportbil — det ingår i uppskattningen.',
    };
  }

  if (counts.sub >= 2) {
    return {
      counts,
      rentalVehicleRequired: true,
      reason: 'two_or_more_subs',
      customerHint: 'Utrustningen kräver större transportbil — det ingår i uppskattningen.',
    };
  }

  if (counts.tops >= 2 && counts.sub >= 1 && counts.flightcaseUnit >= 1) {
    return {
      counts,
      rentalVehicleRequired: true,
      reason: 'tops_sub_and_flightcase',
      customerHint: 'Utrustningen kräver större transportbil — det ingår i uppskattningen.',
    };
  }

  if (!bulkyAudioOrStage && counts.flightcaseUnit > 4) {
    return {
      counts,
      rentalVehicleRequired: true,
      reason: 'flightcase_over_four',
      customerHint: 'Utrustningen kräver större transportbil — det ingår i uppskattningen.',
    };
  }

  return {
    counts,
    rentalVehicleRequired: false,
    reason: 'none',
    customerHint: null,
  };
}
